/**
 * @file Cheques.js
 * @description Page component for listing, filtering, exporting, and managing check cashing records.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import DataTable from "react-data-table-component";
import {
  getCheques,
  deleteCheque,
  backfillMakerCompany,
  syncCompaniesFromCheques,
} from "../api/services";
import { imgUrl } from "../utils/imgUrl";
import toast from "react-hot-toast";
import DateInput from "../components/DateInput";
import {
  formatDateTime,
  capitalizeName,
} from "../utils/formatDate";

/**
 * Formats amount into US currency representation.
 * @param {number|string} n - Dollar amount.
 * @returns {string} Formatted currency.
 */
const fmt = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

const statusColor = {
  Cashed: "badge-green",
  Pending: "badge-yellow",
  Returned: "badge-red",
  Void: "badge-gray",
  Hold: "badge-blue",
};

/**
 * Formats standard 9-digit SSN into XXX-XX-XXXX format.
 * @param {string} val - Raw SSN string.
 * @returns {string} Formatted SSN.
 */
const formatSSN = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 9);
  if (d.length > 5)
    return d.slice(0, 3) + "-" + d.slice(3, 5) + "-" + d.slice(5);
  if (d.length > 3) return d.slice(0, 3) + "-" + d.slice(3);
  return d;
};

/**
 * Modal preview lightbox for check images.
 * @component
 * @param {Object} props
 * @param {Object} props.cheque - Cheque item.
 * @param {Function} props.onClose - Modal close handler.
 */
function ChequeImageModal({ cheque, onClose }) {
  const [side, setSide] = useState(
    cheque?.checkImages?.front ? "front" : "back",
  );
  if (!cheque) return null;
  const hasFront = Boolean(cheque.checkImages?.front);
  const hasBack = Boolean(cheque.checkImages?.back);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: 12,
          padding: "20px 22px",
          maxWidth: "92vw",
          maxHeight: "94vh",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          boxShadow: "0 12px 50px rgba(0,0,0,0.55)",
          minWidth: 340,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              Check #{cheque.checkNumber}
              <span
                style={{
                  color: side === "front" ? "#1a7a4a" : "#495057",
                  fontWeight: 600,
                  marginLeft: 8,
                  textTransform: "capitalize",
                }}
              >
                — {side}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#6c757d", marginTop: 2 }}>
              {cheque.maker || ""}{" "}
              {cheque.amount ? `· $${Number(cheque.amount).toFixed(2)}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {hasFront && (
              <button
                onClick={() => setSide("front")}
                style={{
                  padding: "5px 14px",
                  fontSize: 13,
                  borderRadius: 5,
                  cursor: "pointer",
                  border: "1.5px solid #1a7a4a",
                  background: side === "front" ? "#1a7a4a" : "white",
                  color: side === "front" ? "white" : "#1a7a4a",
                  fontWeight: 600,
                }}
              >
                Front
              </button>
            )}
            {hasBack && (
              <button
                onClick={() => setSide("back")}
                style={{
                  padding: "5px 14px",
                  fontSize: 13,
                  borderRadius: 5,
                  cursor: "pointer",
                  border: "1.5px solid #6c757d",
                  background: side === "back" ? "#6c757d" : "white",
                  color: side === "back" ? "white" : "#6c757d",
                  fontWeight: 600,
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: "5px 12px",
                fontSize: 16,
                borderRadius: 5,
                cursor: "pointer",
                border: "1.5px solid #dee2e6",
                background: "#f8f9fa",
                color: "#495057",
                fontWeight: 700,
                marginLeft: 4,
              }}
            >
              ✕
            </button>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            overflow: "auto",
            textAlign: "center",
            background: "#f1f3f5",
            borderRadius: 8,
            padding: 10,
          }}
        >
          {cheque.checkImages?.[side] ? (
            <img
              key={side}
              src={imgUrl(cheque.checkImages[side])}
              alt={`Check ${side}`}
              style={{
                maxWidth: "100%",
                maxHeight: "72vh",
                borderRadius: 6,
                objectFit: "contain",
                boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
              }}
            />
          ) : (
            <div style={{ padding: 40, color: "#6c757d", fontSize: 14 }}>
              No {side} image attached.
            </div>
          )}
        </div>
        <div style={{ textAlign: "center", fontSize: 11, color: "#adb5bd" }}>
          Click outside or ✕ to close
        </div>
      </div>
    </div>
  );
}

/* ── DataTable styles ─────────────────────────────────────── */
const dtStyles = {
  headRow: {
    style: {
      backgroundColor: "var(--gray-4)",
      borderBottom: "1px solid var(--gray-3)",
      minHeight: "42px",
    },
  },
  headCells: {
    style: {
      fontSize: "11.5px",
      fontWeight: "700",
      color: "var(--gray-1)",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      paddingLeft: "14px",
      paddingRight: "8px",
    },
  },
  rows: {
    style: {
      fontSize: "13px",
      color: "var(--dark)",
      borderBottom: "1px solid var(--gray-4)",
      minHeight: "48px",
      cursor: "pointer",
      "&:hover": { backgroundColor: "rgba(26,122,74,0.04)" },
    },
  },
  cells: { style: { paddingLeft: "14px", paddingRight: "8px" } },
  pagination: {
    style: {
      borderTop: "1px solid var(--gray-3)",
      fontSize: "13px",
      color: "var(--gray-1)",
      minHeight: "52px",
    },
    pageButtonsStyle: {
      borderRadius: "6px",
      color: "var(--dark)",
      fill: "var(--gray-1)",
      "&:hover:not(:disabled)": { backgroundColor: "var(--gray-4)" },
      "&:focus": { outline: "none", backgroundColor: "var(--gray-4)" },
    },
  },
  noData: {
    style: { padding: "40px", color: "var(--gray-1)", fontSize: "13.5px" },
  },
  progress: { style: { padding: "40px" } },
};

const conditionalRowStyles = [
  {
    when: (r) => r.status === "Returned",
    style: { backgroundColor: "rgba(224,60,60,0.04)" },
  },
  {
    when: (r) => r.status === "Hold",
    style: { backgroundColor: "rgba(59,130,246,0.04)" },
  },
  {
    when: (r) => r.status === "Pending",
    style: { backgroundColor: "rgba(240,165,0,0.04)" },
  },
];

const buildColumns = (navigate, setImageModal, handleVoid) => [
  {
    name: "FIRST NAME",
    selector: (r) => r.customer?.firstName,
    sortable: true,
    sortField: "customer",
    minWidth: "120px",
    cell: (r) =>
      r.customer ? (
        <span
          className="table-customer-link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/customers/${r.customer._id}`);
          }}
        >
          {capitalizeName(r.customer.firstName)}
        </span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    name: "LAST NAME",
    selector: (r) => r.customer?.lastName,
    sortable: true,
    sortField: "customerLastName",
    minWidth: "120px",
    cell: (r) =>
      r.customer ? (
        <span
          className="table-customer-link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/customers/${r.customer._id}`);
          }}
        >
          {capitalizeName(r.customer.lastName)}
        </span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    name: "CASHED DATE",
    selector: (r) => r.dateCashed,
    sortable: true,
    sortField: "dateCashed",
    minWidth: "160px",
    cell: (r) => (
      <span className="td-date">
        {r.dateCashed ? formatDateTime(r.dateCashed) : "—"}
      </span>
    ),
  },
  {
    name: "CHECK #",
    selector: (r) => r.checkNumber,
    sortable: true,
    sortField: "checkNumber",
    minWidth: "110px",
    cell: (r) => (
      <span className="td-mono" style={{ fontWeight: 700 }}>
        {r.checkNumber}
      </span>
    ),
  },
  {
    name: "AMOUNT",
    selector: (r) => r.amount,
    sortable: true,
    sortField: "amount",
    right: true,
    minWidth: "100px",
    cell: (r) => <span className="amount">{fmt(r.amount)}</span>,
  },
  {
    name: "FEE",
    selector: (r) => r.fee,
    sortable: true,
    sortField: "fee",
    right: true,
    minWidth: "85px",
    cell: (r) => (
      <span
        className="td-fee"
        style={{ color: "var(--danger)", fontWeight: 600 }}
      >
        {fmt(r.fee)}
      </span>
    ),
  },
  {
    name: "PAY OUT",
    selector: (r) => r.payOut,
    sortable: true,
    sortField: "payOut",
    right: true,
    minWidth: "100px",
    cell: (r) => <span className="amount-positive">{fmt(r.payOut)}</span>,
  },
  {
    name: "MAKER",
    selector: (r) => r.maker,
    sortable: true,
    minWidth: "130px",
    cell: (r) => (
      <span
        style={{
          fontSize: 12,
          maxWidth: 130,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        }}
        title={r.maker}
      >
        {r.maker || "—"}
      </span>
    ),
  },
  {
    name: "STATUS",
    selector: (r) => r.status,
    sortable: true,
    sortField: "status",
    minWidth: "95px",
    cell: (r) => (
      <span className={`badge ${statusColor[r.status] || "badge-gray"}`}>
        {r.status}
      </span>
    ),
  },
  {
    name: "ACTIONS",
    minWidth: "130px",
    cell: (r) => (
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        {(r.checkImages?.front || r.checkImages?.back) && (
          <button
            className="btn btn-sm btn-outline"
            title="View check image"
            onClick={() => setImageModal(r)}
            style={{ padding: "2px 7px" }}
          >
            🖼️
          </button>
        )}
        <button
          className="btn btn-sm btn-outline"
          onClick={() => navigate(`/cheques/${r._id}`)}
        >
          View
        </button>
        {r.status !== "Void" && (
          <button
            className="btn btn-sm btn-ghost"
            style={{ color: "var(--danger)" }}
            onClick={() => handleVoid(r._id)}
          >
            Void
          </button>
        )}
      </div>
    ),
    ignoreRowClick: true,
  },
];

const DEFAULT_LIMIT = 10;

/**
 * FilterChip component for rendering removable filter tag buttons.
 * @component
 * @param {Object} props
 * @param {string} props.label - Display label for filter chip.
 * @param {Function} props.onRemove - Callback when chip remove button is clicked.
 */
function FilterChip({ label, onRemove }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "var(--primary-light)",
        color: "var(--primary)",
        border: "1px solid rgba(26,122,74,0.2)",
        borderRadius: 20,
        padding: "3px 10px 3px 10px",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {label}
      <button
        onClick={onRemove}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--primary)",
          fontSize: 14,
          lineHeight: 1,
          padding: "0 0 1px 2px",
          display: "flex",
          alignItems: "center",
        }}
      >
        ×
      </button>
    </span>
  );
}

/**
 * Main Cheques page component for managing and searching check records.
 * @component
 * @returns {React.ReactElement}
 */
export default function Cheques() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [cheques, setCheques] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [imageModal, setImageModal] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sort, setSort] = useState({ column: {}, direction: "desc" });

  const emptyFilters = {
    search: "",
    customerName: "",
    socialNo: "",
    amountExact: "",
    amountMin: "",
    amountMax: "",
    status: searchParams.get("status") || "",
    checkType: "",
    dateFrom: "",
    dateTo: "",
    dateSearchBy: "dateCashed",
    customerDob: "",
  };
  const [committed, setCommitted] = useState(emptyFilters);
  const [draft, setDraft] = useState({ ...emptyFilters });

  /* ── Fetch ─────────────────────────────────────────────── */
  const lastFetchTimeRef = useRef(0);
  const lastFetchParamsRef = useRef("");

  const fetchCheques = useCallback(async (pg, perPage, sortState, filters) => {
    const sortField = sortState?.column?.sortField || "dateCashed";
    const sortDir =
      sortState?.direction === "asc" ? sortField : `-${sortField}`;
    const params = { page: pg, limit: perPage, sort: sortDir };
    if (filters.search) {
      params.search = filters.search; // matches checkNumber / maker / bank
      params.customerName = filters.search; // matches firstName / lastName (via aggregation $or)
    }
    if (filters.socialNo) params.socialNo = filters.socialNo;
    if (filters.amountExact) params.amountExact = filters.amountExact;
    if (filters.amountMin) params.amountMin = filters.amountMin;
    if (filters.amountMax) params.amountMax = filters.amountMax;
    if (filters.status) params.status = filters.status;
    if (filters.checkType) params.checkType = filters.checkType;
    if (filters.customerDob) params.customerDob = filters.customerDob;
    if (filters.dateFrom || filters.dateTo) {
      params.dateField = filters.dateSearchBy || "dateCashed";
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
    }

    // Deduplicate identical requests within 400ms (React StrictMode double-mount)
    const paramsKey = JSON.stringify(params);
    const now = Date.now();
    if (paramsKey === lastFetchParamsRef.current && now - lastFetchTimeRef.current < 400) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;
    lastFetchTimeRef.current = now;

    setLoading(true);
    try {
      const res = await getCheques(params);
      setCheques(res.data.data || []);
      setSummary(res.data.summary || {});
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCheques(page, limit, sort, committed);
  }, [page, limit, sort, committed, fetchCheques]);

  /* ── Handlers ───────────────────────────────────────────── */
  const handleDraft = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const handleSSNInput = (e) => {
    setDraft((d) => ({ ...d, socialNo: formatSSN(e.target.value) }));
  };

  const handleSearch = () => {
    setPage(1);
    setCommitted({ ...draft });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const clearFilters = () => {
    const empty = { ...emptyFilters, status: "" };
    setDraft(empty);
    setCommitted(empty);
    setPage(1);
    setShowAdvanced(false);
  };

  const handleSort = (column, direction) => {
    setPage(1);
    setSort({ column, direction });
  };

  const handleVoid = async (id) => {
    if (!window.confirm("Void this check?")) return;
    try {
      await deleteCheque(id);
      toast.success("Check voided");
      fetchCheques(page, limit, sort, committed);
    } catch {
      toast.error("Failed to void check");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    const toastId = toast.loading("🔄 Syncing makers & linking cheques...");
    try {
      // Step 1: Sync all maker names into Company Master
      const syncRes = await syncCompaniesFromCheques();
      const { added = [] } = syncRes.data;

      // Step 2: Backfill makerCompany ID on all existing cheques
      const backfillRes = await backfillMakerCompany();
      const { updated = 0, newCompanies = [] } = backfillRes.data;
      void newCompanies;

      toast.success(
        `✅ Sync complete!\n` +
        `• ${added.length} new companies added to master\n` +
        `• ${updated} cheques linked to company ID`,
        { id: toastId, duration: 6000 },
      );

      // Refresh the table
      fetchCheques(page, limit, sort, committed);
    } catch (e) {
      toast.error(e.response?.data?.message || "Sync failed", { id: toastId });
    } finally {
      setSyncing(false);
    }
  };
  void syncing;
  void handleSync;

  const columns = buildColumns(navigate, setImageModal, handleVoid);

  /* ── Active filter chips from committed state ─────────── */
  const activeChips = [];
  if (committed.search)
    activeChips.push({ key: "search", label: `Search: "${committed.search}"` });
  if (committed.status)
    activeChips.push({ key: "status", label: `Status: ${committed.status}` });
  if (committed.socialNo)
    activeChips.push({ key: "socialNo", label: `SSN: ${committed.socialNo}` });
  if (committed.customerDob)
    activeChips.push({
      key: "customerDob",
      label: `DOB: ${committed.customerDob}`,
    });
  if (committed.amountExact)
    activeChips.push({
      key: "amountExact",
      label: `Amount: $${committed.amountExact}`,
    });
  if (committed.amountMin || committed.amountMax)
    activeChips.push({
      key: "amountRange",
      label: `Range: $${committed.amountMin || "0"}–$${committed.amountMax || "∞"}`,
    });
  if (committed.dateFrom || committed.dateTo) {
    const label = `${committed.dateSearchBy === "dateCashed" ? "Cashed" : "Check"} date: ${committed.dateFrom || ""}${committed.dateFrom && committed.dateTo ? "–" : ""}${committed.dateTo || ""}`;
    activeChips.push({ key: "dateRange", label });
  }

  const removeChip = (key) => {
    const reset = {};
    if (key === "amountRange") {
      reset.amountMin = "";
      reset.amountMax = "";
    } else if (key === "dateRange") {
      reset.dateFrom = "";
      reset.dateTo = "";
    } else reset[key] = "";
    const updated = { ...committed, ...reset };
    setDraft({ ...draft, ...reset });
    setCommitted(updated);
    setPage(1);
  };

  /* ── Has any advanced filter active ──────────────────── */
  const hasAdvanced =
    committed.socialNo ||
    committed.customerDob ||
    committed.amountExact ||
    committed.amountMin ||
    committed.amountMax ||
    committed.dateFrom ||
    committed.dateTo;

  return (
    <Layout title="Transactions">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              fontWeight: 800,
            }}
          >
            Transactions
          </h2>
          <p className="text-muted text-sm mt-1">
            {total.toLocaleString("en-US")} total records
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {/* Sync Companies button — uncomment to enable manual sync from Transactions page
          <button
            onClick={handleSync}
            disabled={syncing}
            title="Sync all maker names to Company Master and link cheques by company ID"
            style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"8px 14px", borderRadius:8, fontSize:13, fontWeight:600,
              border:"1.5px solid var(--primary)", background: syncing ? "var(--gray-4)" : "var(--primary-light)",
              color:"var(--primary)", cursor: syncing ? "not-allowed" : "pointer",
              transition:"all 0.15s",
            }}
          >
            {syncing
              ? <><span className="spinner" style={{width:14,height:14,borderWidth:2}} /> Syncing…</>
              : <>⚡ Sync Companies</>
            }
          </button>
          */}
          <Link to="/cheques/new" className="btn btn-primary">
            💵 Cash a Check
          </Link>
        </div>
      </div>

      {/* Summary bar */}
      {!loading && summary.count > 0 && (
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          {[
            {
              label: "Total Volume",
              val: fmt(summary.totalAmount),
              color: "var(--primary)",
            },
            {
              label: "Total Fees",
              val: fmt(summary.totalFee),
              color: "var(--accent)",
            },
            {
              label: "Total Payout",
              val: fmt(summary.totalPayout),
              color: "var(--success)",
            },
            { label: "Transactions", val: summary.count, color: "var(--info)" },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                background: "white",
                borderRadius: 10,
                padding: "12px 18px",
                border: "1px solid var(--gray-3)",
                flex: 1,
                minWidth: 140,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--gray-1)",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {s.label}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 18,
                  fontWeight: 800,
                  color: s.color,
                }}
              >
                {s.val}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Smart Filter Panel ─────────────────────────────── */}
      <div className="card mb-4" style={{ overflow: "visible" }}>
        <div style={{ padding: "14px 16px" }}>
          {/* Primary row: main search + status + date range + Search btn */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {/* Universal search */}
            <div style={{ flex: "2 1 220px" }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--gray-1)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Search
              </label>
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 14,
                    color: "var(--gray-2)",
                    pointerEvents: "none",
                  }}
                >
                  🔍
                </span>
                <input
                  className="form-control"
                  placeholder="Customer name, check #, maker, bank..."
                  value={draft.search}
                  onChange={(e) => handleDraft("search", e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{ paddingLeft: 32 }}
                />
              </div>
            </div>

            {/* Status */}
            <div style={{ flex: "1 1 140px" }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--gray-1)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Status
              </label>
              <select
                className="form-control form-select"
                value={draft.status}
                onChange={(e) => handleDraft("status", e.target.value)}
              >
                <option value="">All Status</option>
                {["Cashed", "Pending", "Returned", "Void", "Hold"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div style={{ flex: "1 1 130px" }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--gray-1)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                From Date
              </label>
              <DateInput
                value={draft.dateFrom}
                onChange={(e) => handleDraft("dateFrom", e.target.value)}
              />
            </div>

            {/* Date To */}
            <div style={{ flex: "1 1 130px" }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--gray-1)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                To Date
              </label>
              <DateInput
                value={draft.dateTo}
                onChange={(e) => handleDraft("dateTo", e.target.value)}
              />
            </div>

            {/* Action buttons */}
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "flex-end",
                flexShrink: 0,
              }}
            >
              <button
                className="btn btn-primary"
                onClick={handleSearch}
                style={{ fontWeight: 700, minWidth: 90, height: 38 }}
              >
                Search
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowAdvanced((v) => !v)}
                title="More filters"
                style={{
                  height: 38,
                  padding: "0 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color:
                    showAdvanced || hasAdvanced
                      ? "var(--primary)"
                      : "var(--gray-1)",
                  border: "1px solid",
                  borderColor:
                    showAdvanced || hasAdvanced
                      ? "var(--primary)"
                      : "var(--gray-3)",
                  background:
                    showAdvanced || hasAdvanced
                      ? "var(--primary-light)"
                      : "transparent",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  position: "relative",
                }}
              >
                ⚙️ More
                {hasAdvanced && (
                  <span
                    style={{
                      position: "absolute",
                      top: -5,
                      right: -5,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--primary)",
                      display: "block",
                    }}
                  />
                )}
              </button>
            </div>
          </div>

          {/* Advanced filters — collapsible */}
          {showAdvanced && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px dashed var(--gray-3)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                gap: 8,
                animation: "fadeIn 0.18s ease",
              }}
            >
              {/* SSN */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--gray-1)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  SSN
                </label>
                <input
                  className="form-control"
                  placeholder="XXX-XX-XXXX"
                  value={draft.socialNo}
                  onChange={handleSSNInput}
                  onKeyDown={handleKeyDown}
                  style={{ fontFamily: "monospace", letterSpacing: "0.5px" }}
                  maxLength={11}
                />
              </div>

              {/* Customer DOB */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--gray-1)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Date of Birth
                </label>
                <DateInput
                  value={draft.customerDob}
                  onChange={(e) => handleDraft("customerDob", e.target.value)}
                />
              </div>

              {/* Amount exact */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--gray-1)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Exact Amount ($)
                </label>
                <input
                  className="form-control"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 306.00"
                  value={draft.amountExact}
                  onChange={(e) => handleDraft("amountExact", e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              {/* Amount range */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--gray-1)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Amount Range ($)
                </label>
                <div style={{ display: "flex", gap: 0 }}>
                  <input
                    className="form-control"
                    type="number"
                    placeholder="Min"
                    value={draft.amountMin}
                    onChange={(e) => handleDraft("amountMin", e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{
                      borderRadius: "var(--radius-sm) 0 0 var(--radius-sm)",
                      borderRight: "none",
                    }}
                  />
                  <input
                    className="form-control"
                    type="number"
                    placeholder="Max"
                    value={draft.amountMax}
                    onChange={(e) => handleDraft("amountMax", e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{
                      borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
                      borderLeft: "1px solid var(--gray-3)",
                    }}
                  />
                </div>
              </div>

              {/* Date field toggle */}
              <div>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--gray-1)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Date Type
                </label>
                <select
                  className="form-control form-select"
                  value={draft.dateSearchBy}
                  onChange={(e) => handleDraft("dateSearchBy", e.target.value)}
                >
                  <option value="dateCashed">Cashed Date</option>
                  <option value="checkDate">Check Date</option>
                </select>
              </div>
            </div>
          )}

          {/* Bottom bar: active chips + clear */}
          {activeChips.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px solid var(--gray-4)",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: "var(--gray-2)",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  marginRight: 2,
                }}
              >
                Active:
              </span>
              {activeChips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={() => removeChip(chip.key)}
                />
              ))}
              <button
                onClick={clearFilters}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--gray-1)",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "3px 8px",
                  marginLeft: "auto",
                  textDecoration: "underline",
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── DataTable ─────────────────────────────────────────── */}
      <div className="card">
        <DataTable
          columns={columns}
          data={cheques}
          progressPending={loading}
          progressComponent={
            <div className="table-loading-cell">
              <div className="spinner spinner-dark spinner-centered" />
            </div>
          }
          noDataComponent={
            <div className="table-empty-cell">
              <div className="empty-icon">📋</div>
              <p>No transactions found. Adjust filters and click Search.</p>
            </div>
          }
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationDefaultPage={page}
          paginationPerPage={limit}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(newLimit, p) => {
            setLimit(newLimit);
            setPage(p);
          }}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={1}
          defaultSortAsc={false}
          onRowClicked={(row) => navigate(`/cheques/${row._id}`)}
          highlightOnHover
          pointerOnHover
          customStyles={dtStyles}
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>

      {imageModal && (
        <ChequeImageModal
          cheque={imageModal}
          onClose={() => setImageModal(null)}
        />
      )}
    </Layout>
  );
}
